import { Node, mergeAttributes, CommandProps } from '@tiptap/core'
import Image from '@tiptap/extension-image'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    resizableImage: {
      setResizableImage: (options: { src: string; 'data-file-id': string; width?: string; height?: string; }) => ReturnType
    }
  }
}

export const ResizableImage = Image.extend({
  name: 'resizableImage',

  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: el => el.getAttribute('src'),
        renderHTML: attrs => attrs.src ? { src: attrs.src } : {},
      },
      'data-file-id': {
        default: null,
        parseHTML: el => el.getAttribute('data-file-id'),
        renderHTML: attrs => attrs['data-file-id'] ? { 'data-file-id': attrs['data-file-id'] } : {},
      },
      width: {
        default: null,
        parseHTML: el => el.getAttribute('width'),
        renderHTML: attrs => attrs.width ? { width: attrs.width } : {},
      },
      height: {
        default: null,
        parseHTML: el => el.getAttribute('height'),
        renderHTML: attrs => attrs.height ? { height: attrs.height } : {},
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'img',
        getAttrs: el => ({
          'data-file-id': el.getAttribute('data-file-id'),
          src: el.getAttribute('src'),
          width: el.getAttribute('width'),
          height: el.getAttribute('height'),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { 'data-file-id': fileId, src, width, height,  } = HTMLAttributes;
    const attrs: Record<string, any> = {};

    if (fileId) attrs['data-file-id'] = fileId; 
    if (src) attrs.src = src;                   
    if (width) attrs.width = width;
    if (height) attrs.height = height;

    return ['img', attrs];
  },

  addCommands() {
    return {
      setResizableImage:
        (options: { src: string; width?: string; height?: string; 'data-file-id': string }) =>
        ({ chain }: CommandProps) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: options,
            })
            .run()
        },
    }
  },

  addNodeView() {
    return (props: any) => {
      const { node, editor, getPos } = props;

      const container = document.createElement('span');
      container.style.position = 'relative';
      container.style.display = 'inline-block';

      const img = document.createElement('img');
      Object.entries(node.attrs).forEach(([key, value]) => {
        if (value != null) img.setAttribute(key, value as string);
      });
      img.style.width = node.attrs.width ?? 'auto';
      img.style.height = node.attrs.height ?? 'auto';
      img.style.maxWidth = '100%';
      img.draggable = true;

      const handle = document.createElement('div');
      handle.style.position = 'absolute';
      handle.style.right = '0';
      handle.style.bottom = '0';
      handle.style.width = '10px';
      handle.style.height = '10px';
      handle.style.cursor = 'nwse-resize';
      handle.style.background = 'rgba(0,0,0,0.3)';
      handle.style.borderRadius = '2px';

      let startX = 0;
      let startWidth = 0;

      const onMouseMove = (e: MouseEvent) => {
        const diff = e.clientX - startX;
        const newWidth = Math.max(50, startWidth + diff);
        const ratio = img.naturalHeight / img.naturalWidth;
        const newHeight = newWidth * ratio;

        img.style.width = `${newWidth}px`;
        img.style.height = `${newHeight}px`;
      };

      const onMouseUp = () => {
        const pos = typeof getPos === 'function' ? getPos() : getPos;
        if (typeof pos === 'number') {
          editor.chain().focus().command(({ tr }) => {
            tr.setNodeMarkup(pos, node.type, {
              ...node.attrs,
              width: img.style.width,
              height: img.style.height,
            });
            return true;
          }).run();
        }
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      handle.addEventListener('mousedown', e => {
        e.preventDefault();
        startX = e.clientX;
        startWidth = img.offsetWidth;

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });

      container.appendChild(img);
      container.appendChild(handle);

      return {
        dom: container,
      };
    };
  },
});
